/**
 * Rendezvous claim contract (SECURE-INVITE-ONBOARDING-PRD §6). The broker side of
 * the secure invite handoff: a joiner who clicked an invite link derives the
 * lookup hash from their code and fetches the sealed credential blob ONCE.
 *
 * Deliberately UNAUTHENTICATED — the joiner isn't a workspace member yet. The
 * security is: the lookup is a 256-bit-derived hash (not guessable), the claim is
 * single-use (read-and-delete), the stash is short-TTL, and the ciphertext is
 * useless without the invite CODE (held only by the joiner) and unforgeable
 * without the fleet key. So serving it to "anyone with the exact lookup" is safe.
 */

import { z } from "zod";
import type { Operation } from "../types";

/** rendezvousKey(code) — a SHA-256 hex digest (64 lowercase hex chars). */
const lookupHash = z.string().regex(/^[0-9a-f]{64}$/, "invalid rendezvous lookup");

const claimQuery = z.object({ k: lookupHash });
const claimResponse = z.object({
  /** The sealed blob, or null when unknown / already claimed / expired. */
  ciphertext: z.string().nullable(),
});

const claimOp: Operation<
  void,
  z.infer<typeof claimQuery>,
  void,
  z.infer<typeof claimResponse>
> = {
  method: "GET",
  path: () => "/api/sync/rendezvous/claim",
  query: claimQuery,
  response: claimResponse,
};

/**
 * Apply a claimed handoff (the JOINER's same-origin endpoint). The client reads
 * the invite link fragment (code + issuer key), fetches the ciphertext from the
 * broker (cross-origin, the claim endpoint above), then posts it HERE — to its
 * OWN server — so the decrypt + local apply never makes an outbound fetch (no
 * SSRF surface). The server openHandoffs, writes sync.env + the pending invite,
 * and hot-flips into replica mode (no restart).
 */
const applyBody = z.object({
  code: z.string().min(16),
  ciphertext: z.string().min(1),
  /** Issuer fleet public key from the link fragment (raw base64url or PEM). */
  issuerKey: z.string().min(1),
  /** Optional out-of-band host binding (only when the broker IS the primary). */
  expectedHost: z.string().optional(),
});
const applyResponse = z.object({
  ok: z.boolean(),
  /** true = replica active now; false = config written, restart to apply. */
  hotFlipped: z.boolean(),
  workspace: z.string().nullable(),
  reason: z.string().optional(),
});
const applyOp: Operation<
  void,
  void,
  z.infer<typeof applyBody>,
  z.infer<typeof applyResponse>
> = {
  method: "POST",
  path: () => "/api/sync/join-secure",
  body: applyBody,
  response: applyResponse,
};

export const rendezvous = { claim: claimOp, apply: applyOp };
